import { useState } from 'react'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { PlacementId } from '../../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { useEmbroideryAvailability } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryAvailabilityProvider'
import { useEmbroideryGarment } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryGarmentProvider'
import { useEmbroiderySelection } from '../../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import {
  EmbroideryItem,
  EmbroideryItemOfType,
  TextItemValues,
} from '../../../../../../containers/EmbroideryContainer/providers/types'
import { genericTextEmbroideryThreadColors } from '../../../../../../containers/EmbroideryContainer/utils/embroideryThreadOptions'
import {
  useRestrictedEmbroideryColors,
  useRestrictedEmbroideryFonts,
} from '../../../../../../containers/EmbroideryContainer/utils/isEmbroideryColorAllowedForProduct'
import { reportSelectionRefused } from '../../../../../../containers/EmbroideryContainer/utils/reportEmbroideryError'
import { isLightProductColor } from '../../../../../../containers/EmbroideryContainer/utils/unorganized'
import { PanelBody } from '../../../styles'
import { FormHeader } from '../../FormHeader/FormHeader'
import { ModalFooter } from '../../ModalFooter/ModalFooter'
import { PlacementOccupants } from '../../PlacementOccupants/PlacementOccupants'
import { PlacementPicker } from '../../PlacementPicker/PlacementPicker'
import { useShowDraftInPreview } from '../../preview/PreviewDraftContext'
import { TextColorPicker } from '../TextColorPicker/TextColorPicker'
import { TextFontPicker } from '../TextFontPicker/TextFontPicker'
import { TextLineFields } from '../TextLineFields/TextLineFields'

import { AddButton, PlacementForm } from './styles'

export type TextPlacementEditorProps = {
  maxPlacements: number
  onDone(): void
  onBack(): void
}

/**
 * Text embroidery across several placements: a card per placement, each holding its own lines,
 * thread color and font.
 */
export const TextPlacementEditor: React.FC<TextPlacementEditorProps> = ({
  maxPlacements,
  onDone,
  onBack,
}) => {
  const { items, saveItemsOfType } = useEmbroiderySelection()
  const {
    embroidery: {
      modal: {
        backButton: backLabel,
        forms: { submissionButtons, textOption, titles, validationErrorMessages },
      },
      placement,
    },
  } = useTranslation()

  const { matrix } = useEmbroideryAvailability()
  // "Select up to N" only says something when the garment offers more placements than N.
  const showInstructions =
    matrix.text.placements.filter(placement => placement.offered).length > maxPlacements
  // Temporary unblocking of colors for DTC. Remove this in LFE-187
  const availableColors = useRestrictedEmbroideryColors([])
  // Temporary unblocking of fonts for DTC. Remove this in LFE-188
  const availableFonts = useRestrictedEmbroideryFonts([])

  const { productColorHex } = useEmbroideryGarment()
  const contrastKey = (
    isLightProductColor(productColorHex)
      ? genericTextEmbroideryThreadColors.Black
      : genericTextEmbroideryThreadColors.White
  ).key

  const emptyText = (): TextItemValues => ({
    firstLine: '',
    secondLine: '',
    font: availableFonts[0],
    threadColor: availableColors.find(color => color.key === contrastKey) ?? availableColors[0],
  })

  const savedTextItems = items.filter(
    (item): item is EmbroideryItemOfType<'text'> => item.typeKey === 'text'
  )
  const textItemAt = (placementId: PlacementId) =>
    savedTextItems.find(item => item.placementId === placementId)

  /** One card's worth of text for each of `textItems`, keyed by placement. */
  const cardsFrom = (textItems: readonly EmbroideryItemOfType<'text'>[]) =>
    Object.fromEntries(textItems.map(item => [item.placementId, item.values])) as Partial<
      Record<PlacementId, TextItemValues>
    >

  /**
   * The checked cards and their text, starting from what the garment carries. Kept apart from the
   * selection until "Update garment", so leaving the editor any other way leaves the garment as it
   * was.
   */
  const [cards, setCards] = useState(() => cardsFrom(savedTextItems))
  // Text from unchecked cards, shown if the card is opened and restored when it is checked again.
  const [uncheckedValues, setUncheckedValues] = useState<
    Partial<Record<PlacementId, TextItemValues>>
  >({})
  const [expandedPlacementId, setExpandedPlacementId] = useState<PlacementId | undefined>()
  const [lineErrors, setLineErrors] = useState<Partial<Record<PlacementId, string>>>({})
  const [groupError, setGroupError] = useState<string | undefined>()

  const selectedPlacementIds = Object.keys(cards) as PlacementId[]
  const valuesAt = (placementId: PlacementId): TextItemValues | undefined => cards[placementId]
  const hasText = (values: TextItemValues | undefined): values is TextItemValues =>
    !!values && (values.firstLine.trim() !== '' || !!values.secondLine?.trim())

  const keyAt = (placementId: PlacementId) =>
    textItemAt(placementId)?.instanceId ?? `draft-text-${placementId}`

  // Every checked card counts against the rules, so the picker closes what saving would refuse. The
  // open card counts once it has text; until then moving to another card unchecks it, so it can't
  // be what blocks that card.
  const editorItems: EmbroideryItem[] = [
    ...items.filter(item => item.typeKey !== 'text'),
    ...selectedPlacementIds
      .filter(placementId => placementId !== expandedPlacementId || hasText(valuesAt(placementId)))
      .map(
        (placementId): EmbroideryItemOfType<'text'> => ({
          instanceId: keyAt(placementId),
          typeKey: 'text',
          placementId,
          values: valuesAt(placementId) ?? emptyText(),
          source: 'USER',
          enabledForBag: true,
        })
      ),
  ]

  useShowDraftInPreview({
    typeKey: 'text',
    // A blank card draws "Your Text Here" only while it is open; collapsed, it draws nothing.
    marks: selectedPlacementIds
      .filter(placementId => placementId === expandedPlacementId || hasText(valuesAt(placementId)))
      .map(placementId => ({
        key: keyAt(placementId),
        placementId,
        typeKey: 'text',
        values: valuesAt(placementId) ?? emptyText(),
      })),
    // The close-up shows only while the open card holds text, typed or saved; otherwise the garment.
    focusedPlacementId:
      expandedPlacementId && hasText(valuesAt(expandedPlacementId))
        ? expandedPlacementId
        : undefined,
  })

  /** Checks the card with `values`, or unchecks it when there are none. */
  const setCard = (placementId: PlacementId, values: TextItemValues | undefined) =>
    setCards(current => {
      const next = { ...current }
      if (values === undefined) delete next[placementId]
      else next[placementId] = values
      return next
    })

  const togglePlacement = (placementId: PlacementId) => {
    if (selectedPlacementIds.includes(placementId)) {
      // The flow put that text there, and the customer may not take it away.
      if (textItemAt(placementId)?.source === 'ENFORCED') return

      const values = valuesAt(placementId)
      if (hasText(values)) setUncheckedValues(current => ({ ...current, [placementId]: values }))
      setCard(placementId, undefined)
      setLineErrors(current => ({ ...current, [placementId]: undefined }))
      if (expandedPlacementId === placementId) setExpandedPlacementId(undefined)
      return
    }

    openCard(placementId)
  }

  /** Closes the active card, unchecking it if the customer left it without typing anything. */
  const closeActiveCard = () => {
    if (expandedPlacementId === undefined) return

    const values = valuesAt(expandedPlacementId)
    const isEnforced = textItemAt(expandedPlacementId)?.source === 'ENFORCED'
    if (values && !hasText(values) && !isEnforced) {
      setCard(expandedPlacementId, undefined)
      setLineErrors(current => ({ ...current, [expandedPlacementId]: undefined }))
    }
    setExpandedPlacementId(undefined)
  }

  /** Opens a card, checking it, and closes whichever card was open before. */
  const openCard = (placementId: PlacementId) => {
    closeActiveCard()
    if (!selectedPlacementIds.includes(placementId)) {
      setCard(placementId, uncheckedValues[placementId] ?? emptyText())
    }
    setExpandedPlacementId(placementId)
  }

  const toggleExpanded = (placementId: PlacementId) =>
    expandedPlacementId === placementId ? closeActiveCard() : openCard(placementId)

  /** Shows why the card's text can't be saved, if it can't. Returns whether it can. */
  const validateCard = (placementId: PlacementId, values: TextItemValues): boolean => {
    const error =
      values.firstLine.trim().length === 0 ? validationErrorMessages.requiredFirstLine : undefined
    setLineErrors(current => ({ ...current, [placementId]: error }))
    return error === undefined
  }

  /** Closes the card once its text is valid. Nothing reaches the selection until "Update garment". */
  const commit = (placementId: PlacementId) => {
    // Opening a card checks it, so the open card always has values here.
    const values = valuesAt(placementId)
    if (!values || !validateCard(placementId, values)) return

    setGroupError(undefined)
    setExpandedPlacementId(undefined)
  }

  const renderPlacementForm = (placementId: PlacementId) => {
    const values = valuesAt(placementId) ?? uncheckedValues[placementId] ?? emptyText()
    // Saving refuses a change to enforced text, so its fields offer none to make.
    const isEnforced = textItemAt(placementId)?.source === 'ENFORCED'

    return (
      <PlacementForm>
        <PlacementOccupants typeKey='text' placementId={placementId} dividers='after' />

        <TextLineFields
          placementId={placementId}
          values={values}
          errors={{ firstLine: lineErrors[placementId] }}
          onChange={next => setCard(placementId, next)}
          disabled={isEnforced}
        />

        <TextColorPicker
          selectedColor={values.threadColor}
          onSelectColor={threadColor => setCard(placementId, { ...values, threadColor })}
          disabled={isEnforced}
        />

        <TextFontPicker
          selectedFontKey={values.font?.key}
          onSelectFont={font => setCard(placementId, { ...values, font })}
          disabled={isEnforced}
        />

        <AddButton type='button' onClick={() => commit(placementId)}>
          {submissionButtons.applyCta}
        </AddButton>
      </PlacementForm>
    )
  }

  // With every card unchecked, saving takes the text off the garment, so it needs text there to take.
  const canSave = selectedPlacementIds.length > 0 || savedTextItems.length > 0

  const save = () => {
    if (!canSave) {
      setGroupError(validationErrorMessages.requiredTextSelection)
      return
    }

    // Validate every card, in the picker's order, so the first one to fix is the one that opens.
    const invalid = matrix.text.placements
      .map(({ placementId }) => placementId)
      .filter(placementId => cards[placementId] && !validateCard(placementId, cards[placementId]))
    if (invalid.length > 0) {
      setExpandedPlacementId(invalid[0])
      return
    }

    // A refusal means the picker offered a card the rules forbid, so there is no message for it.
    const saved = saveItemsOfType(
      'text',
      selectedPlacementIds.map(placementId => ({
        instanceId: textItemAt(placementId)?.instanceId,
        placementId,
        values: cards[placementId]!,
      }))
    )
    if (!saved.ok) {
      reportSelectionRefused(saved.reason, 'text', selectedPlacementIds)
      return
    }

    setGroupError(undefined)
    onDone()
  }

  /** Empties the editor of everything but what the flow enforces, for "Update garment" to save. */
  const reset = () => {
    setCards(cardsFrom(savedTextItems.filter(item => item.source === 'ENFORCED')))
    setUncheckedValues({})
    setLineErrors({})
    setGroupError(undefined)
    setExpandedPlacementId(undefined)
  }

  return (
    <>
      <PanelBody $fillPanel={false}>
        <FormHeader title={titles.text} onBack={onBack} backLabel={backLabel} />

        <PlacementPicker
          mode='multi'
          typeKey='text'
          showPrice={true}
          label={placement.textPlacementLabel}
          instructions={
            showInstructions
              ? textOption.placementInstructions.replace('{{max}}', String(maxPlacements))
              : undefined
          }
          errorMessage={groupError}
          items={editorItems}
          selectedPlacementIds={selectedPlacementIds}
          expandedPlacementId={expandedPlacementId}
          onTogglePlacement={togglePlacement}
          onToggleExpanded={toggleExpanded}
          renderPlacementForm={renderPlacementForm}
        />
      </PanelBody>

      <ModalFooter
        primaryLabel={submissionButtons.updateGarmentCta}
        onPrimary={save}
        primaryDisabled={!canSave}
        secondaryLabel={submissionButtons.resetCta}
        onSecondary={reset}
        sticky={false}
      />
    </>
  )
}
